import { type Grade } from "@core/grades/types";
import { type City } from "@core/shared/types";
import { arabicNamePattern, URLS } from "@core/shared/utils";
import { SubmitButton } from "@design-system/components/Button";
import {
  EmailInput,
  PasswordInput,
  PhoneNumberInput,
  SelectInput,
  TextInput,
} from "@design-system/components/Form";
import { BaseLink, ReturnToHome } from "@design-system/components/link";
import {
  useCreateAccount,
  useCreateAccountGroups,
} from "@framework/auth/hooks";
import cache from "@mongez/cache";
import { trans, plainTrans } from "@mongez/localization";
import { Form } from "@mongez/react-form";
import { useState } from "react";
import { settingsAtom } from "../../../framework/remix/shared/atoms";
import { LoginBrowserHelp } from "./LoginBrowserHelp";

export function RegisterForm({
  grades,
  cities,
}: {
  grades: Grade[];
  cities: City[];
}) {
  const register = useCreateAccount({
    onSuccess: () => {
      cache.remove("registerData");
    },
  });
  const { groups, isLoading, updateGroups, id } = useCreateAccountGroups();
  const [selectedGrade, setSelectedGrade] = useState<Grade>();
  const [registerData] = useState(() => {
    if (typeof window === "undefined") return {};

    return cache.get("registerData") || {};
  });

  const account = settingsAtom.use("account");
  const teachingLanguages =
    settingsAtom.use("general")?.teachingLanguages || [];

  const handleGradeChange = (gradeId: string) => {
    const grade = grades.find((g) => g.id.toString() === gradeId);

    setSelectedGrade(grade);
    updateGroups();
  };

  const updateData = (key, value) => {
    const data = cache.get("registerData") || {};
    data[key] = value;
    cache.set("registerData", data);
  };

  return (
    <div className="w-full space-y-8">
      <ReturnToHome />

      <div className="space-y-1.5">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {trans("auth.createAccount")}
        </h2>
        <p className="text-sm text-muted-foreground dark:text-gray-300">
          {trans("auth.createAccountDescription")}
        </p>
      </div>

      <Form onSubmit={register} className="space-y-6">
        <LoginBrowserHelp />
        {/* <div className="space-y-4"> */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextInput
            name="name"
            pattern={account?.arabicNamesOnly ? arabicNamePattern : undefined}
            minWords={3}
            onChange={(value) => updateData("name", value)}
            defaultValue={registerData.name}
            errors={{
              minWords: trans("auth.minNameWords"),
              pattern: trans("auth.arabicNamesOnly"),
            }}
            label={trans("auth.name")}
            placeholder={plainTrans("auth.namePlaceholder")}
            required
            maxLength={50}
          />

          {!account?.hideCity && cities?.length > 0 && (
            <SelectInput
              name="city"
              onChange={(value) => updateData("city", value)}
              label={trans("auth.city")}
              placeholder={plainTrans("auth.city")}
              required
              options={cities.map((city) => ({
                label: city.name,
                value: city.id.toString(),
              }))}
            />
          )}
          {/* </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2"> */}
          <EmailInput
            name="email"
            label={trans("auth.email")}
            placeholder={plainTrans("auth.emailPlaceholder")}
            onChange={(value) => updateData("email", value)}
            defaultValue={registerData.email}
          />
          <PhoneNumberInput
            name="phoneNumber"
            label={trans("auth.phoneNumber")}
            placeholder={plainTrans("auth.phoneNumberPlaceholder")}
            required
            defaultValue={registerData.phoneNumber}
            onChange={(value) => updateData("phoneNumber", value)}
          />
          <PhoneNumberInput
            label={trans("parentPhoneNumber")}
            name="parentPhoneNumber"
            placeholder={plainTrans("parentPhoneNumber")}
            defaultValue={registerData.parentPhoneNumber}
            onChange={(value) => updateData("parentPhoneNumber", value)}
            required
          />
          <PasswordInput
            name="password"
            label={trans("auth.password")}
            placeholder={plainTrans("auth.password")}
            required
          />
          <PasswordInput
            name="confirmPassword"
            match="password"
            label={trans("auth.confirmPassword")}
            placeholder={plainTrans("auth.confirmPassword")}
            required
          />
          {/* </div> */}
          {/* <div className="grid grid-cols-1 gap-4 md:grid-cols-2"> */}
          {teachingLanguages.length > 0 && (
            <SelectInput
              name="teachingLanguage"
              label={trans("teachingLanguage")}
              placeholder={plainTrans("teachingLanguage")}
              required
              searchable={false}
              onChange={updateGroups}
              options={teachingLanguages.map((language) => ({
                label: trans(`teachingLanguages.${language}`),
                value: language,
              }))}
            />
          )}
          <SelectInput
            name="gender"
            label={trans("gender")}
            placeholder={plainTrans("gender")}
            required
            searchable={false}
            onChange={updateGroups}
            defaultValue={registerData.gender}
            options={["male", "female"].map((gender) => ({
              label: trans(gender),
              value: gender,
            }))}
          />
          <SelectInput
            name="grade"
            label={trans("auth.grade")}
            placeholder={plainTrans("auth.grade")}
            required
            searchable={false}
            onChange={handleGradeChange}
            autoSelectSingleOption
            options={grades.map((grade) => ({
              label: grade.name,
              value: grade.id.toString(),
            }))}
          />
          {selectedGrade?.majors && selectedGrade.majors.length > 0 && (
            <SelectInput
              name="major"
              label={trans("auth.major")}
              placeholder={plainTrans("auth.majorPlaceholder")}
              required
              searchable={false}
              options={selectedGrade.majors.map((major) => ({
                label: trans(`auth.${major}`),
                value: major,
              }))}
            />
          )}
          {/* </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2"> */}
          <SelectInput
            name="studentGroup"
            label={trans("auth.group")}
            placeholder={plainTrans("auth.group")}
            required
            loading={isLoading}
            autoSelectSingleOption
            key={id}
            options={groups.map((group) => ({
              label: group.name,
              value: group.id.toString(),
            }))}
          />
          {account?.allowCustomId && (
            <TextInput
              name="customId"
              label={trans("account.customId")}
              defaultValue={registerData.customId}
              placeholder={plainTrans("account.customId")}
              onChange={(value) => updateData("customId", value)}
            />
          )}
          {/* </div> */}
        </div>
        <div className="space-y-4">
          <SubmitButton className="h-11 w-full bg-primary text-primary-foreground shadow-lg transition-all duration-300 hover:scale-105 hover:bg-primary/90 hover:shadow-xl dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700">
            {trans("auth.createAccount")}
          </SubmitButton>
          <p className="flex gap-2 text-gray-600 dark:text-gray-400">
            {trans("auth.alreadyHaveAccount")}
            <BaseLink
              to={URLS.auth.login}
              className="text-primary hover:text-primary/80 dark:text-primary dark:hover:text-primary/80"
            >
              {trans("auth.login")}
            </BaseLink>
          </p>
        </div>
      </Form>
    </div>
  );
}
